import { useState, useEffect, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Package } from "lucide-react";
import type { ComboWithDetails, ComboSlotWithProducts } from "@/hooks/usePublicCombos";
import type { PublicComboSelection } from "@/hooks/usePublicCart";

interface PublicComboDetailSheetProps {
  combo: ComboWithDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddToCart: (
    combo: ComboWithDetails,
    price: number,
    selection: PublicComboSelection,
    quantity: number,
    notes: string
  ) => void;
}

type SlotSelection = Map<string, { productId: string; productName: string; price: number }[]>;

export function PublicComboDetailSheet({
  combo,
  open,
  onOpenChange,
  onAddToCart,
}: PublicComboDetailSheetProps) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [slotSelections, setSlotSelections] = useState<SlotSelection>(new Map());

  // Reset state when combo changes
  useEffect(() => {
    if (combo) {
      setQuantity(1);
      setNotes("");
      setSlotSelections(new Map());
    }
  }, [combo?.id]);

  const slots = combo?.slots ?? [];
  const basePrice = combo?.price ?? 0;

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);

  // Calculate total price based on slot selections
  const calculatedPrice = useMemo(() => {
    let total = 0;
    let hasCalculatedSlots = false;

    for (const slot of slots) {
      const selections = slotSelections.get(slot.id) || [];

      // Handle fixed product slots
      if (slot.slot_type === "product" && slot.product) {
        if (slot.pricing_rule === "fixed" && slot.fixed_price !== null) {
          total += slot.fixed_price;
        } else {
          total += slot.product.price;
        }
        hasCalculatedSlots = true;
        continue;
      }

      // Handle category slots with selections
      if (selections.length > 0) {
        const prices = selections.map((s) => s.price);
        hasCalculatedSlots = true;

        switch (slot.pricing_rule) {
          case "highest":
            total += Math.max(...prices);
            break;
          case "average":
            total += prices.reduce((sum, p) => sum + p, 0) / prices.length;
            break;
          case "fixed":
            total += (slot.fixed_price ?? 0) * selections.length;
            break;
          case "rodizio":
            // Rodizio uses base price
            break;
        }
      }
    }

    // If no calculated slots, use base price
    return hasCalculatedSlots ? total : basePrice;
  }, [slots, basePrice, slotSelections]);

  const totalPrice = calculatedPrice * quantity;

  // Validate if all required slots have selections within min/max
  const isValidSelection = useMemo(() => {
    if (!combo) return false;

    for (const slot of slots) {
      // Fixed product slots are always valid
      if (slot.slot_type === "product") continue;

      const selections = slotSelections.get(slot.id) || [];
      const count = selections.length;

      // Check required slots have at least min selections
      if (slot.is_required && count < slot.min_quantity) {
        return false;
      }

      // Check max quantity is not exceeded
      if (count > slot.max_quantity) {
        return false;
      }
    }
    return true;
  }, [combo, slots, slotSelections]);

  // Handle product selection in a category slot
  const handleProductToggle = (
    slot: ComboSlotWithProducts,
    product: { id: string; name: string; price: number }
  ) => {
    setSlotSelections(current => {
      const newMap = new Map(current);
      const currentSelections = newMap.get(slot.id) || [];
      const existingIndex = currentSelections.findIndex(s => s.productId === product.id);

      if (existingIndex >= 0) {
        // Remove selection
        const updated = currentSelections.filter(s => s.productId !== product.id);
        newMap.set(slot.id, updated);
      } else {
        // Add selection if within max
        if (currentSelections.length < slot.max_quantity) {
          newMap.set(slot.id, [
            ...currentSelections,
            { productId: product.id, productName: product.name, price: product.price }
          ]);
        } else if (slot.max_quantity === 1) {
          // Single selection - replace
          newMap.set(slot.id, [
            { productId: product.id, productName: product.name, price: product.price }
          ]);
        }
      }

      return newMap;
    });
  };

  const handleAddToCart = () => {
    if (!combo) return;
    if (!isValidSelection) return;

    // Convert slot selections to the cart format
    const selection: PublicComboSelection = {
      slotSelections: Object.fromEntries(
        Array.from(slotSelections.entries()).map(([slotId, products]) => [
          slotId,
          products.map((p) => ({
            productId: p.productId,
            productName: p.productName,
            price: p.price,
          })),
        ])
      ),
    };

    onAddToCart(combo, calculatedPrice, selection, quantity, notes);
    onOpenChange(false);
  };

  const getPricingRuleLabel = (rule: string) => {
    switch (rule) {
      case 'highest': return 'Maior valor';
      case 'average': return 'Valor médio';
      case 'fixed': return 'Preço fixo';
      case 'rodizio': return 'Rodízio';
      default: return rule;
    }
  };

  if (!combo) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {combo.name}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-6">
          {/* Image */}
          {combo.image_url && (
            <div className="w-full h-48 rounded-lg overflow-hidden">
              <img
                src={combo.image_url}
                alt={combo.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Description */}
          {combo.description && (
            <p className="text-muted-foreground">{combo.description}</p>
          )}

          {/* Price */}
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-primary">
              {formatPrice(calculatedPrice)}
            </span>
          </div>

          {/* Slots */}
          {combo.slots.length > 0 && (
            <div className="space-y-6">
              {combo.slots.map((slot) => (
                <div key={slot.id} className="space-y-3">
                  {/* Fixed product slot */}
                  {slot.slot_type === 'product' && slot.product && (
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <Badge variant="outline">Incluído</Badge>
                      <span>{slot.product.name}</span>
                      {slot.pricing_rule === 'fixed' && slot.fixed_price !== null && (
                        <span className="ml-auto text-sm font-medium">
                          {formatPrice(slot.fixed_price)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Category slot - product selection */}
                  {slot.slot_type === 'category' && slot.products && slot.products.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">
                            {slot.label || slot.category?.name || 'Escolha'}
                          </h3>
                          {slot.is_required && (
                            <Badge variant="destructive" className="text-xs">
                              Obrigatório
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {getPricingRuleLabel(slot.pricing_rule)}
                          </Badge>
                          <Badge>
                            {slotSelections.get(slot.id)?.length || 0}/{slot.max_quantity}
                          </Badge>
                        </div>
                      </div>

                      {slot.min_quantity > 0 && (
                        <p className="text-sm text-muted-foreground">
                          Escolha de {slot.min_quantity} até {slot.max_quantity} opções
                        </p>
                      )}

                      {/* Single selection - use radio */}
                      {slot.max_quantity === 1 ? (
                        <RadioGroup
                          value={slotSelections.get(slot.id)?.[0]?.productId || ''}
                          onValueChange={(value) => {
                            const product = slot.products?.find(p => p.id === value);
                            if (product) {
                              handleProductToggle(slot, product);
                            }
                          }}
                        >
                          <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                            {slot.products.map((product) => (
                              <div
                                key={product.id}
                                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:border-muted-foreground"
                              >
                                <RadioGroupItem
                                  value={product.id}
                                  id={`slot-${slot.id}-product-${product.id}`}
                                />
                                <Label
                                  htmlFor={`slot-${slot.id}-product-${product.id}`}
                                  className="flex-1 cursor-pointer"
                                >
                                  {product.name}
                                </Label>
                                <span className="text-sm font-medium">
                                  {formatPrice(product.price)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </RadioGroup>
                      ) : (
                        /* Multiple selection - use checkboxes */
                        <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                          {slot.products.map((product) => {
                            const isSelected = slotSelections.get(slot.id)?.some(
                              s => s.productId === product.id
                            ) || false;
                            const currentCount = slotSelections.get(slot.id)?.length || 0;
                            const canSelect = currentCount < slot.max_quantity;

                            return (
                              <div
                                key={product.id}
                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                  isSelected
                                    ? "border-primary bg-primary/10"
                                    : canSelect
                                    ? "hover:border-muted-foreground"
                                    : "opacity-50"
                                }`}
                                onClick={() => (isSelected || canSelect) && handleProductToggle(slot, product)}
                              >
                                <Checkbox checked={isSelected} />
                                <div className="flex-1">
                                  <p className="font-medium">{product.name}</p>
                                </div>
                                <span className="text-sm font-medium">
                                  {formatPrice(product.price)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              placeholder="Alguma observação especial?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Quantity */}
          <div className="flex items-center justify-between">
            <span className="font-medium">Quantidade</span>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center font-medium">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity(quantity + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Add to Cart Button */}
          <Button
            className="w-full h-12"
            onClick={handleAddToCart}
            disabled={!isValidSelection}
          >
            Adicionar • {formatPrice(totalPrice)}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
